import { Sheet, SheetContent } from "@resume-studio/ui";
import { cn } from "@resume-studio/utils";
import {
  Activity,
  BarChart3,
  ExternalLink,
  Gauge,
  MessageSquare,
  Megaphone,
  ScrollText,
  Users,
} from "lucide-react";
import * as React from "react";
import { Link, useLocation } from "react-router-dom";

type NavItem = {
  href: string;
  label: string;
  icon: typeof Gauge;
};

type NavSection = {
  label: string;
  items: NavItem[];
};

// Root-relative — the admin app lives at the root of admin.resumestudio.io
// so the previous `/admin/*` prefix is gone.
const navSections: NavSection[] = [
  {
    label: "Operate",
    items: [
      { href: "/", label: "Overview", icon: Gauge },
      { href: "/campaigns", label: "Campaigns", icon: Megaphone },
      { href: "/users", label: "Users", icon: Users },
      { href: "/revenue", label: "Revenue", icon: BarChart3 },
    ],
  },
  {
    label: "Insight",
    items: [
      { href: "/analytics", label: "Analytics", icon: Activity },
      { href: "/feedback", label: "Feedback", icon: MessageSquare },
      { href: "/audit", label: "Audit log", icon: ScrollText },
    ],
  },
];

const APP_URL =
  (import.meta.env.VITE_PUBLIC_APP_URL as string | undefined) ?? "http://localhost:5173";

interface AdminSidebarProps {
  /** Mobile sheet open state (desktop sidebar is always shown on lg+). */
  mobileOpen: boolean;
  onMobileOpenChange: (open: boolean) => void;
}

/**
 * Stripe-Dashboard-flavoured sidebar.
 *
 * Spec:
 * - 248px wide on desktop, white background, single 1px right border.
 * - Section headers: 11px uppercase, `tracking-[0.14em] text-subtle`.
 * - Active item: 2px `primary` (indigo-600) bar on the left edge,
 *   `bg-surface` background, NO rounded pill, NO icon recolouring (the
 *   left bar is the affordance).
 * - Hover: subtle `bg-surface-muted` only.
 *
 * Mobile (< lg): hidden by default; opens as a left-side Sheet driven by
 * `mobileOpen`. We share one nav body between desktop and mobile so the
 * active-item highlight, link hovers, and section ordering stay identical.
 */
export const AdminSidebar = ({ mobileOpen, onMobileOpenChange }: AdminSidebarProps) => {
  const { pathname } = useLocation();

  // Auto-close the mobile drawer on navigation so the user lands on the new
  // page instead of staring at the still-open sheet.
  const lastPathRef = React.useRef(pathname);
  React.useEffect(() => {
    if (lastPathRef.current !== pathname) {
      lastPathRef.current = pathname;
      if (mobileOpen) onMobileOpenChange(false);
    }
  }, [pathname, mobileOpen, onMobileOpenChange]);

  return (
    <>
      <aside className="fixed left-0 top-0 z-40 hidden h-screen w-[248px] flex-col border-r border-border bg-card lg:flex">
        <SidebarBody pathname={pathname} />
      </aside>

      <Sheet open={mobileOpen} onOpenChange={onMobileOpenChange}>
        <SheetContent
          side="left"
          className="flex w-[280px] flex-col border-r border-border bg-card p-0 lg:hidden"
        >
          <SidebarBody pathname={pathname} />
        </SheetContent>
      </Sheet>
    </>
  );
};

const SidebarBody = ({ pathname }: { pathname: string }) => (
  <>
    <div className="flex h-14 items-center gap-2 border-b border-border px-5">
      <Link to="/" className="flex items-center gap-2.5 transition-opacity hover:opacity-80">
        <span className="flex size-7 items-center justify-center rounded-md bg-primary text-primary-foreground">
          <Gauge className="size-3.5" />
        </span>
        <div className="leading-none">
          <p className="text-[13px] font-semibold text-foreground">Resume Studio</p>
          <p className="mt-0.5 text-[10px] font-medium uppercase tracking-[0.16em] text-subtle">
            Admin
          </p>
        </div>
      </Link>
    </div>

    <nav className="flex-1 overflow-y-auto px-3 py-5">
      {navSections.map((section, sectionIdx) => (
        <div key={section.label} className={cn(sectionIdx > 0 && "mt-6")}>
          <p className="mb-1 px-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-subtle">
            {section.label}
          </p>
          <ul className="space-y-px">
            {section.items.map((item) => {
              const isActive =
                item.href === "/" ? pathname === "/" : pathname.startsWith(item.href);
              const Icon = item.icon;

              return (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    className={cn(
                      "relative flex items-center gap-2.5 rounded-sm px-3 py-1.5 text-[13px] transition-colors duration-120 ease-out",
                      isActive
                        ? "bg-surface font-medium text-foreground"
                        : "text-muted-foreground hover:bg-surface-muted hover:text-foreground",
                    )}
                  >
                    {isActive && (
                      <span
                        aria-hidden
                        className="absolute inset-y-1.5 left-0 w-[2px] rounded-full bg-primary"
                      />
                    )}
                    <Icon className="size-4 shrink-0 text-subtle" />
                    <span className="flex-1 truncate">{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>

    <div className="border-t border-border px-3 py-3">
      <a
        href={`${APP_URL}/resumes`}
        target="_blank"
        rel="noreferrer"
        className="flex items-center gap-2 rounded-sm px-3 py-1.5 text-[12px] text-muted-foreground transition-colors duration-120 ease-out hover:bg-surface-muted hover:text-foreground"
      >
        <ExternalLink className="size-3.5" />
        Open consumer app
      </a>
    </div>
  </>
);
